import { BrowserSessionApiClient } from './browserSessionApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');

export type BydaWorkspaceEnquiryRecord = {
  bydaEnquiryId: number;
  project: {
    projectId: number;
    projectNumber: string | null;
    projectName: string | null;
    siteAddress: string | null;
    suburb: string | null;
    postCode: string | null;
    council: string | null;
    siteId?: string | null;
    driveId?: string | null;
    folderItemId?: string | null;
    folderPath?: string | null;
  } | null;
  createdBy?: string | null;
  localStatus: string;
  userReference: string | null;
  digStartAt: string | null;
  digEndAt: string | null;
  activityTypes: string[];
  locationTypes: string[];
  source: string | null;
  isSandboxTest: boolean;
  remoteEnquiryId: number | null;
  remoteExternalId: string | null;
  shareUrl: string | null;
  lastSyncedAt: string | null;
  remoteCreatedAt: string | null;
  remoteUpdatedAt: string | null;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  createdDate: string | null;
  modifiedDate: string | null;
};

export type BydaSharePointTarget = {
  integrationStatus: 'configured' | 'not_configured' | 'error';
  exists: boolean;
  driveId: string | null;
  projectFolderPath: string | null;
  folderPath: string | null;
  message: string | null;
  error: string | null;
};

export type BydaResultDocument = {
  bydaResultFileId?: number | null;
  sourceType: 'response_file' | 'response_attachment';
  referralId: number | null;
  responseId: number | null;
  remoteFileId: number;
  fileName: string;
  mimeType: string | null;
  downloadUrl: string | null;
  responseCreatedAt: string | null;
  blobFileId?: string | null;
  blobStatus?: 'not_staged' | 'staged' | 'stage_failed' | 'not_ready';
  blobStoredAt?: string | null;
  sharePointStatus: 'not_filed' | 'filed' | 'upload_failed' | 'not_ready' | 'skipped';
  sharePointWebUrl: string | null;
  sharePointPath: string | null;
  notes: string[];
};

export type BydaWebhookEventRecord = {
  bydaWebhookEventId: number;
  eventUuid: string | null;
  eventType: string | null;
  remoteEnquiryId: number | null;
  remoteExternalId: string | null;
  userReference: string | null;
  bydaEnquiryId: number | null;
  projectId: number | null;
  signatureHeader: string | null;
  lastOutcome: string | null;
  processingNotes: string | null;
  createdDate: string | null;
  modifiedDate: string | null;
};

export type BydaEnquiryDetail = {
  record: BydaWorkspaceEnquiryRecord | null;
  remoteSyncError: { code: string; message: string } | null;
  resultDocuments: BydaResultDocument[];
  sharePointTarget: BydaSharePointTarget | null;
  resultWarnings: string[];
  webhookEvents: BydaWebhookEventRecord[];
  uploadedCount?: number;
  failedCount?: number;
};

export type BydaYvwSewerAssetFeature = {
  id: string;
  layer: 'SEWERPIPES' | 'SEWERBRANCHES' | 'SEWERACCESSPOINTS';
  layerLabel: string;
  geometryType: string | null;
  geometry: Record<string, unknown> | null;
  summary: Record<string, unknown>;
  properties: Record<string, unknown>;
};

export type BydaYvwSewerInfo = {
  source: {
    provider: string;
    service: string;
    url: string;
    attribution: string;
    disclaimer: string;
  };
  fetchedAt: string;
  bydaEnquiryId: number;
  project: {
    projectId: number | null;
    projectNumber: string | null;
    projectName: string | null;
    siteAddress: string | null;
    suburb: string | null;
    postCode: string | null;
  };
  search: {
    originalBbox: number[];
    subjectBoundary?: BydaPolygon | null;
    bbox: number[];
    bufferMeters: number;
    srsName: string;
    outputFormat: string;
    limitPerLayer: number;
  };
  counts: {
    total: number;
    sewerPipes: number;
    sewerBranches: number;
    sewerAccessPoints: number;
  };
  pipeAssets: BydaYvwSewerAssetFeature[];
  branchAssets: BydaYvwSewerAssetFeature[];
  accessPointAssets: BydaYvwSewerAssetFeature[];
  warnings: string[];
  geoJson: {
    type: 'FeatureCollection';
    crs?: Record<string, unknown>;
    features: Array<Record<string, unknown>>;
  };
};

export type BydaUsagePolicyMember = {
  code: string;
  label: string;
  email: string;
  usedToday: number;
  remainingToday: number;
  dailyLimit: number;
  usedThisMonth: number;
};

export type BydaUsagePolicySummary = {
  actor: {
    email: string | null;
    code: string | null;
    label: string | null;
    isTracked: boolean;
    usedToday: number;
    remainingToday: number;
  };
  timezone: string;
  localDate: string;
  localMonth: string;
  limits: {
    perUserPerDay: number;
    totalPerDay: number;
    totalPerMonth: number;
  };
  members: BydaUsagePolicyMember[];
  totals: {
    usedToday: number;
    remainingToday: number;
    usedThisMonth: number;
    remainingThisMonth: number;
  };
};

export type BydaFormOption = {
  code: string;
  label: string;
  sequence: number | null;
};

export type BydaAuthorityOption = {
  id: number;
  label: string;
  sequence: number | null;
};

export type BydaFormOptions = {
  enquiryTypes: BydaFormOption[];
  activityDomain: string;
  activityTypes: BydaFormOption[];
  locationTypes: BydaFormOption[];
  locationsInRoad: BydaFormOption[];
  authorityOptions: BydaAuthorityOption[];
  authorityOptionsWarning?: string | null;
};

export type BydaDuplicateSummary = {
  isDuplicate: boolean;
  normalizedAddress: string;
  matchedRecord: {
    bydaEnquiryId: number;
    remoteEnquiryId: number | null;
    userReference: string | null;
    localStatus: string;
    createdDate: string | null;
    createdBy: string | null;
    projectId: number | null;
    projectNumber: string | null;
    projectName: string | null;
    shareUrl: string | null;
  } | null;
};

export type BydaSubmissionPreview = {
  policy: BydaUsagePolicySummary;
  duplicate: BydaDuplicateSummary;
  canSubmit: boolean;
  blockedReason:
    | 'actor_not_tracked'
    | 'daily_user_limit_reached'
    | 'daily_total_limit_reached'
    | 'monthly_total_limit_reached'
    | 'duplicate_address'
    | null;
  warnings: string[];
  requiresConfirmation: true;
};

export type BydaPolygon = {
  type: 'Polygon';
  coordinates: number[][][];
  bbox?: number[];
};

export type CreateBydaEnquiryInput = {
  projectId: number;
  userReference?: string | null;
  digStartAt: string;
  digEndAt: string;
  shape: BydaPolygon;
  isPlanningJob?: boolean | null;
  activityTypes: string[];
  authorityId?: number | null;
  otherAuthorityName?: string | null;
  notes?: string | null;
  locationTypes: string[];
  locationsInRoad?: string[] | null;
  isSandboxTest?: boolean | null;
  autoRenewUntil?: string | null;
  userTimezone?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    locality?: string | null;
    state?: string | null;
    country?: string | null;
      postcode?: string | number | null;
    } | null;
  confirmSubmission?: boolean | null;
};

function unwrapData<T>(value: any): T {
  return (value?.data ?? value) as T;
}

class BydaService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  async listEnquiries(input?: {
    projectId?: number | null;
    limit?: number | null;
  }): Promise<BydaWorkspaceEnquiryRecord[]> {
    const response = await this.browserClient.request('/api/v1/byda/enquiries', {
      query: {
        projectId: input?.projectId ?? undefined,
        limit: input?.limit ?? 24,
      },
    });

    return unwrapData<{ records: BydaWorkspaceEnquiryRecord[] }>(response)?.records || [];
  }

  async claimAcceptance(id: number): Promise<void> {
    await this.browserClient.request(`/api/v1/byda/enquiries/${id}/claim`, { method: 'POST' });
  }

  async createEnquiry(input: CreateBydaEnquiryInput): Promise<BydaWorkspaceEnquiryRecord> {
    const response = await this.browserClient.request('/api/v1/byda/enquiries', {
      method: 'POST',
      body: input,
    });

    return unwrapData<{ record: BydaWorkspaceEnquiryRecord }>(response)?.record;
  }

  async getEnquiryDetail(bydaEnquiryId: number, input?: {
    refreshRemote?: boolean;
    includeResults?: boolean;
  }): Promise<BydaEnquiryDetail> {
    const params = new URLSearchParams();
    if (typeof input?.refreshRemote === 'boolean') {
      params.set('refreshRemote', String(input.refreshRemote));
    }
    if (typeof input?.includeResults === 'boolean') {
      params.set('includeResults', String(input.includeResults));
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/byda/enquiries/${bydaEnquiryId}${suffix}`);
    return unwrapData<BydaEnquiryDetail>(response);
  }

  async fileEnquiryResults(bydaEnquiryId: number): Promise<BydaEnquiryDetail> {
    const response = await this.browserClient.request(`/api/v1/byda/enquiries/${bydaEnquiryId}/file-results`, {
      method: 'POST',
    });
    return unwrapData<BydaEnquiryDetail>(response);
  }

  async getYvwSewerInfo(bydaEnquiryId: number): Promise<BydaYvwSewerInfo> {
    const response = await this.browserClient.request(`/api/v1/byda/enquiries/${bydaEnquiryId}/yvw-sewer-info`);
    return unwrapData<{ sewerInfo: BydaYvwSewerInfo }>(response)?.sewerInfo;
  }

  async getPolicySummary(): Promise<BydaUsagePolicySummary> {
    const response = await this.browserClient.request('/api/v1/byda/policy');
    return unwrapData<{ policy: BydaUsagePolicySummary }>(response)?.policy;
  }

  async getFormOptions(input: {
    projectId: number;
    isPlanningJob?: boolean | null;
    shape?: BydaPolygon | null;
  }): Promise<BydaFormOptions> {
    const response = await this.browserClient.request('/api/v1/byda/form-options', {
      query: {
        projectId: input.projectId,
        isPlanningJob: input.isPlanningJob ?? false,
        shape: input.shape ? JSON.stringify(input.shape) : undefined,
      },
    });
    return unwrapData<{ options: BydaFormOptions }>(response)?.options;
  }

  async previewEnquiry(input: CreateBydaEnquiryInput): Promise<BydaSubmissionPreview> {
    const response = await this.browserClient.request('/api/v1/byda/enquiries/preview', {
      method: 'POST',
      body: input,
    });
    return unwrapData<{ preview: BydaSubmissionPreview }>(response)?.preview;
  }
}

export const bydaService = new BydaService();
